import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  HostListener,
  Inject,
  Injector
} from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Message, MessageAction, User } from '@progress/kendo-angular-conversational-ui';
import { TextAreaComponent } from '@progress/kendo-angular-inputs';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ChatService } from 'src/app/core/service/chat.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import insertTextAtCursor from 'insert-text-at-cursor';
import copyToClipboard from 'copy-to-clipboard';
import { ChatEmoji, ChatEmojiCategory, ChatMessagePart } from '../model/chat-emoji';
import { ChatHistoryReplyPreview } from '../model/chat-history-reply-preview';
import { ChatMessageReaction, ChatReactionGroup } from '../model/chat-message-reaction';
import { ChatEmojiService } from '../chat-emoji/chat-emoji.service';
import { LanguageService } from 'src/app/core/service/language.service';
import { LocalStorageService } from 'ngx-webstorage';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { getEmojiKey } from '../chat-emoji/chat-emoji-key';
import { ChatEmojiLookup } from '../chat-emoji/chat-emoji-lookup';
import {
  chatIdsMatch,
  computeHistoryMerge,
  getMessageReactions,
  getMessageReplyToId,
  getReplyTargetMessageId,
  mapHistoryMessage,
  parseChatCountPayload,
  reactionsEqual
} from './chat-history-message.util';
import {
  applyChatScrollAnchor,
  captureChatScrollAnchor,
  capturePrependScrollAnchor,
  getChatScrollElement,
  pinChatScrollToBottom,
  restorePrependScrollAnchor,
  scrollToChatMessage,
  scrollToChatMessageWhenPresent
} from './chat-scroll.util';

@Component({
  standalone: false,
  selector: 'app-chat-history',
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss']
})
export class ChatHistoryComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @ViewChild('emojiPickerAnchor', { static: false }) public emojiPickerAnchor: ElementRef<HTMLButtonElement>;

  @Input() public isWindow = false;
  @Input() public windowHeight: number;
  @Input() public users = [];

  @ViewChild(TooltipDirective) private tooltipDir: TooltipDirective;
  @ViewChild('chatEmojiTooltipHost', { read: TooltipDirective }) private chatEmojiTooltip?: TooltipDirective;
  @ViewChild('messageBoxInput', { static: false }) private messageBoxInput: TextAreaComponent;
  @ViewChild('messageBoxInput', { static: false, read: ElementRef }) private messageBoxEl: ElementRef;
  @ViewChild('emojiSearchInput', { static: false }) private emojiSearchInput: ElementRef<HTMLInputElement>;
  @ViewChild('chatHistoryHost', { static: false }) private chatHistoryHost: ElementRef<HTMLElement>;

  public selectedChat = null;
  public innerInputFocus = false;
  public currentUser: User = null;
  public chatHistory: Message[] = [];
  public textMessage = '';
  public textChatUpdated = '';
  public chatHeight = '100vh';
  public deleteConfirmationOpened = false;
  public deleteHistoryItem = null;
  public emojiPickerOpen = false;
  public emojiPickerMode: 'compose' | 'reaction' = 'compose';
  public chatEmojiCategories: ChatEmojiCategory[] = [];
  public filteredEmojiCategories: ChatEmojiCategory[] = [];
  public emojiSearchTerm = '';
  public emojisLoading = true;
  public recentEmojis: ChatEmoji[] = [];
  public historyLoadingOlder = false;
  public readonly emojiAnchorAlign = { horizontal: 'right', vertical: 'top' };
  public readonly emojiPopupAlign = { horizontal: 'right', vertical: 'bottom' };
  public readonly emojiPopupMargin = { horizontal: 0, vertical: 8 };

  /** Disables Kendo Chat default right-click actions (copy, reply). */
  public readonly messageContextMenuActions: MessageAction[] = [];

  public replyToMessage: Message | null = null;
  public editingMessage: Message | null = null;

  private readonly replyPreviewMaxLength = 120;
  private messagingSubscription: Subscription;
  private readonly emojiLookup = new ChatEmojiLookup();
  private readonly messagePartsCache = new Map<string, ChatMessagePart[]>();
  private readonly recentEmojisStorageKey = 'chat-recent-emojis';
  private readonly recentEmojisLimit: number;
  private reactionTargetMessage: Message | null = null;
  private reactionPickerAnchorElement: HTMLElement | null = null;

  /** Row element kept visually expanded while the delete confirmation dialog is open (Kendo clears bubble selection on focus move). */
  private pendingDeleteMessageRow: HTMLElement | null = null;

  /** Row element kept visually expanded while the reaction emoji picker is open. */
  private pendingReactionMessageRow: HTMLElement | null = null;

  /** Row element kept visually expanded while a message is being edited in the compose box. */
  private pendingEditMessageRow: HTMLElement | null = null;

  /** History reload requested over WebSocket while editing; applied when edit mode ends. */
  private historyReloadDeferred = false;
  private deferredHistoryReloadMarkRead = false;
  private scrollAnchorAfterHistoryLoad: { nearBottom: boolean; scrollTop: number } | null = null;

  /** Ignores out-of-order getChatHistory responses when multiple reloads overlap. */
  private historyLoadGeneration = 0;
  private historyHasMoreOlder = false;
  private historyScrollHandler: (() => void) | null = null;
  private pendingScrollToMessageId: string | number | null = null;
  private scrollToTargetLoadAttempts = 0;
  private readonly scrollToTargetMaxLoadAttempts = 100;

  private readonly typingMessageIdPrefix = 'typing-';
  private readonly typingSendDebounceMs = 400;
  /** Stop notifying peers after this long without input (pause while composing). */
  private readonly typingIdleMs = 8000;
  /** Re-send typing:true while composing so peers keep the indicator visible. */
  private readonly typingHeartbeatMs = 2500;
  /** Fallback if typing:false is never received (lost WebSocket). */
  private readonly typingRemoteExpiryMs = 12000;
  private typingSendTimer: ReturnType<typeof setTimeout> | null = null;
  private typingIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private typingHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private typingNotificationActive = false;
  private readonly typingRemoteExpiryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly remoteTypingByUserId = new Map<string, { userId: number | string; userName?: string }>();
  private onlineUserIds = new Set<string>();
  private onlineStatusLoaded = false;
  private onlineStatusSubscription: Subscription | null = null;
  /** Set while user toggles Mark as unread/read so ws reload does not auto-clear unread. */
  private suppressMarkReadForChatId: string | null = null;
  /** Rendered avatar size (px) for Kendo chat message rows (must match SCSS .k-avatar). */
  private readonly chatAvatarPixelSize = 32;
  private readonly initialsAvatarUrlCache = new Map<string, string>();
  /** Unsent compose text (and optional reply) keyed by chat id so drafts do not leak across chats/spaces. */
  private readonly composeDraftByChatId = new Map<string, { text: string; replyToMessageId?: string | number }>();
  private messageListResizeObserver: ResizeObserver | null = null;
  /** While true, list height changes pin scroll to bottom (reactions, new messages). */
  private stickScrollToBottom = false;

  /**
   * Kendo only renders the status template when `message.status` is truthy.
   * Use ZWSP so we can still show delete beside the timestamp for non-"updated" rows.
   */
  private readonly STATUS_PLACEHOLDER = '\u200B';

  constructor(
    private chatService: ChatService,
    private authenticationService: AuthenticationService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private translate: TranslateService,
    private userService: UserService,
    private chatEmojiService: ChatEmojiService,
    private languageService: LanguageService,
    private storageService: LocalStorageService,
    private appConfigService: AppConfigService,
    private changeDetectorRef: ChangeDetectorRef,
    private injector: Injector,
    @Inject(APP_BASE_HREF) private baseHref: string)
  {
    this.recentEmojisLimit = this.resolveRecentEmojisLimit();
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (!message?.type)
        {
          return;
        }

        if (message.type === 'reloadChatHistory')
        {
          this.handleReloadChatHistoryRequest(message.data);
        }
        else if (message.type === 'wsChatCount')
        {
          this.handleWsChatCount(message.data);
        }
        else if (message.type === 'wsChatTyping')
        {
          this.handleWsChatTyping(message.data);
        }
        else if (message.type === 'wsUsersPresence')
        {
          this.handleWsUsersPresence(message.data);
        }
        else if (message.type === 'suppressOpenChatMarkRead')
        {
          const chatId = message.data?.chatId;
          this.suppressMarkReadForChatId = chatId != null ? String(chatId) : null;
        }
      }
    });
  }

  public get emojiPickerAnchorElement(): HTMLElement | null
  {
    if (this.emojiPickerMode === 'reaction')
    {
      return this.reactionPickerAnchorElement;
    }

    return this.emojiPickerAnchor?.nativeElement ?? null;
  }

  public get emojiPickerAnchorAlign(): { horizontal: string; vertical: string }
  {
    if (this.emojiPickerMode === 'reaction' && this.reactionTargetMessage)
    {
      return this.isCurrentUser(this.reactionTargetMessage)
        ? { horizontal: 'right', vertical: 'top' }
        : { horizontal: 'left', vertical: 'top' };
    }

    return this.emojiAnchorAlign;
  }

  public get emojiPickerPopupAlign(): { horizontal: string; vertical: string }
  {
    if (this.emojiPickerMode === 'reaction' && this.reactionTargetMessage)
    {
      return this.isCurrentUser(this.reactionTargetMessage)
        ? { horizontal: 'right', vertical: 'bottom' }
        : { horizontal: 'left', vertical: 'bottom' };
    }

    return this.emojiPopupAlign;
  }

  public get emojiPickerAriaLabel(): string
  {
    return this.emojiPickerMode === 'reaction' ? 'CHAT-ADD-REACTION' : 'CHAT-ADD-EMOJI';
  }

  @Input() public set chat(value: any)
  {
    this.persistComposeDraftForChat(this.selectedChat?.id);
    this.stopTypingHeartbeat();
    this.stopTypingNotification(true);
    this.clearRemoteTypingIndicators();
    this.closeEmojiPicker();
    this.cancelEdit();
    this.clearDeferredHistoryReload();
    this.selectedChat = value;
    this.chatHistory = [];
    this.historyHasMoreOlder = false;
    this.historyLoadingOlder = false;
    this.pendingScrollToMessageId = null;
    this.scrollToTargetLoadAttempts = 0;
    this.teardownHistoryScrollListener();
    this.clearComposeInput();
    this.historyLoadGeneration++;
    this.loadChatHistory(!!value?.unread);
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void
  {
    if (!this.emojiPickerOpen)
    {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('.chat-emoji-picker-wrap') || target?.closest('.chat-emoji-picker')
      || target?.closest('.chat-history-reaction-btn') || target?.closest('.chat-reaction-chip'))
    {
      return;
    }

    this.closeEmojiPicker();
  }

  @HostListener('document:contextmenu', ['$event'])
  public onMessageContextMenu(event: MouseEvent): void
  {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.k-chat .k-message'))
    {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  public ngOnInit(): void
  {
    this.chatEmojiService.getEmojis().subscribe({
      next: (data) =>
      {
        this.chatEmojiCategories = data.categories;
        this.emojiLookup.build(data.categories);
        this.messagePartsCache.clear();
        this.loadRecentEmojis();
        this.updateFilteredEmojis();
        this.emojisLoading = false;
        this.refreshMessageEmojiRendering();
      },
      error: () =>
      {
        this.chatEmojiCategories = [];
        this.filteredEmojiCategories = [];
        this.emojisLoading = false;
      }
    });

    this.translate.get(['CHAT-MESSAGE', 'CHAT-UPDATED']).subscribe({
      next: (res) =>
      {
        this.textChatUpdated = res['CHAT-UPDATED'];
        this.textMessage = res['CHAT-MESSAGE'];
        if (this.authenticationService.currentUser)
        {
          this.userService.getUserById(this.authenticationService.currentUser.id, true).subscribe({
            next: (user) =>
            {
              this.currentUser = {
                id: this.authenticationService.currentUser.id,
                name: this.authenticationService.currentUser.name,
                avatarUrl: user.photo ? `data:image/jpeg;base64,${user.photo}` : null
              };
            }
          });
        }
      }
    });

    this.refreshOnlineStatus();
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes.users && this.users?.length)
    {
      this.refreshOnlineStatus();
    }

    if (changes.selectedChat)
    {
      afterNextRender(() => this.setupMessageListResizeObserver(), { injector: this.injector });
    }
  }

  public ngAfterViewInit(): void
  {
    this.setupMessageListResizeObserver();
  }

  public ngOnDestroy(): void
  {
    this.teardownHistoryScrollListener();
    this.messageListResizeObserver?.disconnect();
    this.messageListResizeObserver = null;
    this.stickScrollToBottom = false;
    this.stopTypingHeartbeat();
    this.stopTypingNotification(true);
    this.clearRemoteTypingIndicators();
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
    this.onlineStatusSubscription?.unsubscribe();
    this.onlineStatusSubscription = null;
  }

  public deleteHistory(item: any, event?: Event): void
  {
    this.clearPendingDeleteUi();
    this.deleteHistoryItem = item;
    const target = event?.target;
    if (target instanceof HTMLElement)
    {
      const row = target.closest<HTMLElement>('.k-message');
      if (row)
      {
        row.classList.add('chat-history-pending-delete');
        this.pendingDeleteMessageRow = row;
      }
    }
    this.deleteConfirmationOpened = true;
  }

  public onDeleteDialogClose(): void
  {
    this.clearPendingDeleteUi();
    this.deleteConfirmationOpened = false;
  }

  public sendMessage(): void
  {
    if (this.editingMessage)
    {
      this.saveEditedMessage();
      return;
    }

    const messageBox = this.messageBoxInput;
    const newMessage = messageBox.value;

    if (!newMessage)
    {
      return;
    }

    this.stopTypingNotification(true);

    const messageDateTime = moment();
    const replyToId = this.getReplyTargetMessageId(this.replyToMessage);
    const message: any = {
      id: null,
      author: this.currentUser,
      content: newMessage,
      text: newMessage,
      chatId: this.selectedChat.id,
      status: this.STATUS_PLACEHOLDER,
      timestamp: messageDateTime.toDate(),
      updated: false
    };

    if (replyToId != null)
    {
      message.replyToId = replyToId;
    }

    // Create a persisted message
    const chatMessageToPersist: any = cloneDeep(message);
    delete chatMessageToPersist.author.avatarUrl;  // Do not send avatar data to server
    delete chatMessageToPersist.timestamp;
    delete chatMessageToPersist.status;
    chatMessageToPersist.chatDateTime = messageDateTime.utc().toISOString();

    this.chatService.addChatHistory(this.selectedChat.id, chatMessageToPersist).subscribe({
      next: (data: any) =>
      {
        message.id = data.id;   // Refresh message id
        const persistedReplyToId = this.getMessageReplyToId(data);
        if (persistedReplyToId != null)
        {
          message.replyToId = persistedReplyToId;
        }

        delete message.chatId;  // Remove chatId that was required on addChatHistory only
        delete message.chatDateTime;
        const scrollAnchor = this.captureChatScrollAnchor();
        this.chatHistory = [...this.chatHistory, message];
        this.restoreChatScrollAnchor(scrollAnchor);
        if (this.selectedChat?.id != null)
        {
          this.composeDraftByChatId.delete(String(this.selectedChat.id));
        }

        this.clearComposeInput();
        messageBox.focus();
        this.markSelectedChatRead();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public clearValue(): void
  {
    if (this.editingMessage)
    {
      this.cancelEdit();
      return;
    }

    if (!this.messageBoxInput)
    {
      return;
    }

    this.messageBoxInput.value = null;
    this.stopTypingNotification(true);
    this.persistComposeDraftForActiveChat();
    this.messageBoxInput.focus();
    this.innerInputFocus = true;
  }

  public onKeyDown(e: KeyboardEvent): void
  {
    if (e.key === 'Escape' && this.editingMessage)
    {
      e.preventDefault();
      this.cancelEdit();
      return;
    }

    if (e.key === 'Escape' && this.replyToMessage)
    {
      e.preventDefault();
      this.cancelReply();
      return;
    }

    const isEnter = e.key === 'Enter';

    if (!isEnter)
    {
      return;
    }

    const newLine = e.metaKey || e.ctrlKey;
    const enterOnly = !(e.shiftKey || e.metaKey || e.ctrlKey);

    if (enterOnly && this.innerInputFocus)
    {
      e.preventDefault();
      this.sendMessage();
    }

    if (newLine)
    {
      this.messageBoxInput.value += `\r\n`;
    }
  }

  public onInputFocus(): void
  {
    this.innerInputFocus = true;
  }

  public onInputBlur(): void
  {
    setTimeout(() => this.handleComposeFocusChange(), 0);
  }

  public onMessageBoxValueChange(): void
  {
    this.persistComposeDraftForActiveChat();
    const hasText = Boolean(this.messageBoxInput?.value?.trim());
    if (hasText)
    {
      if (this.typingNotificationActive)
      {
        this.resetTypingIdleTimer();
      }
      this.scheduleTypingNotification(true);
    }
    else
    {
      this.stopTypingNotification(true);
    }
  }

  public toggleEmojiPicker(event: Event): void
  {
    event.stopPropagation();
    const willOpen = !this.emojiPickerOpen;
    this.emojiPickerMode = 'compose';
    this.reactionTargetMessage = null;
    this.reactionPickerAnchorElement = null;
    this.emojiPickerOpen = willOpen;

    if (!willOpen)
    {
      this.resetEmojiSearch();
      return;
    }

    setTimeout(() => this.emojiSearchInput?.nativeElement?.focus());
  }

  public openReactionPicker(message: Message, event: Event): void
  {
    event.stopPropagation();
    const trigger = event.currentTarget;
    let anchor: HTMLElement | null = null;

    if (trigger instanceof HTMLElement)
    {
      anchor = trigger.closest('.k-bubble') ?? trigger.closest('.k-message');
    }

    if (!(anchor instanceof HTMLElement))
    {
      return;
    }

    this.emojiPickerMode = 'reaction';
    this.reactionTargetMessage = message;
    this.reactionPickerAnchorElement = anchor;
    this.markPendingReactionRow(event);
    this.emojiPickerOpen = true;
    this.resetEmojiSearch();
    setTimeout(() => this.emojiSearchInput?.nativeElement?.focus());
  }

  public closeEmojiPicker(): void
  {
    this.emojiPickerOpen = false;
    this.emojiPickerMode = 'compose';
    this.reactionTargetMessage = null;
    this.reactionPickerAnchorElement = null;
    this.clearPendingReactionUi();
    this.resetEmojiSearch();
  }

  public onEmojiSearchInput(event: Event): void
  {
    this.emojiSearchTerm = (event.target as HTMLInputElement).value;
    this.updateFilteredEmojis();
  }

  public selectEmoji(emoji: ChatEmoji, event: Event): void
  {
    event.stopPropagation();

    if (this.emojiPickerMode === 'reaction' && this.reactionTargetMessage)
    {
      const reactionKey = getEmojiKey(emoji);
      if (reactionKey)
      {
        this.toggleReaction(this.reactionTargetMessage, reactionKey, emoji);
      }
      return;
    }

    const textarea = this.getMessageTextarea();

    if (textarea)
    {
      insertTextAtCursor(textarea, ChatEmojiLookup.getInsertText(emoji));
      this.messageBoxInput.value = textarea.value;
    }
    else if (this.messageBoxInput)
    {
      this.messageBoxInput.value = `${this.messageBoxInput.value || ''}${ChatEmojiLookup.getInsertText(emoji)}`;
    }

    this.messageBoxInput?.focus();
    this.innerInputFocus = true;
    this.rememberRecentEmoji(emoji);
    this.closeEmojiPicker();
  }

  public getReactionGroups(message: Message): ChatReactionGroup[]
  {
    const reactions = getMessageReactions(message);
    const groups = new Map<string, ChatReactionGroup>();

    reactions.forEach((reaction) =>
    {
      let group = groups.get(reaction.emojiKey);
      if (!group)
      {
        group = {
          emojiKey: reaction.emojiKey,
          emoji: this.emojiLookup.getByKey(reaction.emojiKey) ?? null,
          count: 0,
          userNames: [],
          includesCurrentUser: false
        };
        groups.set(reaction.emojiKey, group);
      }

      group.count++;
      if (reaction.userName)
      {
        group.userNames.push(reaction.userName);
      }

      if (String(reaction.userId) === String(this.currentUser?.id))
      {
        group.includesCurrentUser = true;
      }
    });

    return [...groups.values()];
  }

  public getReactionTooltip(group: ChatReactionGroup): string
  {
    if (group.userNames.length > 0)
    {
      return group.userNames.join(', ');
    }

    return this.getReactionEmojiLabel(group);
  }

  public getReactionEmojiLabel(group: ChatReactionGroup): string
  {
    if (group.emoji)
    {
      return this.getEmojiLabel(group.emoji);
    }

    return group.emojiKey;
  }

  public onReactionChipClick(message: Message, group: ChatReactionGroup, event: Event): void
  {
    event.stopPropagation();
    this.toggleReaction(message, group.emojiKey, group.emoji ?? undefined);
  }

  public getEmojiLabel(emoji: ChatEmoji): string
  {
    const lang = this.languageService.currentLanguage.toLowerCase();
    return lang.startsWith('es') ? emoji.es : emoji.en;
  }

  /** Locale-aware label for the meta row (mirrors Kendo Chat timestamp formatting). */
  public getMessageTimestamp(message: Message): string
  {
    const ts = message?.timestamp;
    if (!ts)
    {
      return '';
    }

    return moment(ts).format('L LT');
  }

  public copyMessage(message: Message, event: Event): void
  {
    event.stopPropagation();
    const text = this.getMessageCopyText(message);
    if (!text)
    {
      return;
    }

    copyToClipboard(text);
    this.messageHandlerService.displaySuccess('COPIED-TO-CLIPBOARD');
  }

  public startReply(message: Message, event: Event): void
  {
    event.stopPropagation();
    if (message?.id == null)
    {
      return;
    }

    this.closeEmojiPicker();
    this.cancelEdit();
    this.replyToMessage = message;
    this.persistComposeDraftForActiveChat();
    setTimeout(() => this.messageBoxInput?.focus());
  }

  public cancelReply(): void
  {
    this.replyToMessage = null;
    this.persistComposeDraftForActiveChat();
  }

  public canEditMessage(message: Message): boolean
  {
    return this.isCurrentUser(message)
      && !this.isTypingIndicatorMessage(message)
      && message?.id != null;
  }

  public startEdit(message: Message, event: Event): void
  {
    event.stopPropagation();
    if (!this.canEditMessage(message))
    {
      return;
    }

    this.closeEmojiPicker();
    this.cancelReply();
    this.persistComposeDraftForActiveChat();
    this.editingMessage = message;
    this.markPendingEditRow(event);
    if (this.messageBoxInput)
    {
      this.messageBoxInput.value = this.getMessageCopyText(message);
    }

    setTimeout(() =>
    {
      this.scrollToMessage(message.id);
      this.messageBoxInput?.focus();
    });
  }

  public cancelEdit(): void
  {
    if (!this.editingMessage)
    {
      return;
    }

    this.editingMessage = null;
    this.clearPendingEditUi();
    if (this.messageBoxInput)
    {
      this.messageBoxInput.value = null;
    }

    this.stopTypingNotification(true);
    this.restoreComposeDraftForChat(this.selectedChat?.id);
    this.flushDeferredHistoryReload();
  }

  /** One-to-one chat (not a space, at most two members). */
  public isDirectChat(): boolean
  {
    if (this.selectedChat?.chatSpace != null)
    {
      return false;
    }

    return (this.selectedChat?.users?.length ?? 0) <= 2;
  }

  public showChatMemberCount(): boolean
  {
    return !this.isDirectChat() && (this.selectedChat?.users?.length ?? 0) > 1;
  }

  /** Multi-user chat (not a space); header lists other participants by first name. */
  public isGroupChat(): boolean
  {
    return this.selectedChat?.chatSpace == null && !this.isDirectChat();
  }

  public getGroupChatHeaderMembers(): { userId: number | string; firstName: string }[]
  {
    const currentUserId = this.authenticationService.currentUser?.id;
    return (this.selectedChat?.users ?? [])
      .filter((user: { userId?: number | string }) =>
        user?.userId != null && !chatIdsMatch(user.userId, currentUserId))
      .map((user: { userId: number | string; firstName?: string; name?: string }) => ({
        userId: user.userId,
        firstName: this.getChatMemberFirstName(user)
      }));
  }

  public isUserOnline(userId: number | string): boolean
  {
    return userId != null && this.onlineUserIds.has(String(userId));
  }

  public isTypingIndicatorMessage(message: Message): boolean
  {
    return this.isTypingMessage(message);
  }

  public getTypingIndicatorDisplayName(message: Message): string
  {
    const userId = message?.author?.id;
    const chatPeer = this.selectedChat?.users?.find((user: { userId?: number | string; firstName?: string; name?: string }) =>
      userId != null && chatIdsMatch(user.userId, userId));
    const cached = userId != null ? this.findCachedUser(userId) : undefined;
    return this.getChatMemberFirstName({
      userId,
      firstName: chatPeer?.firstName,
      name: message?.author?.name ?? chatPeer?.name ?? cached?.name
    });
  }

  public getDirectChatPeerPhotoUrl(): string | null
  {
    const peerId = this.getDirectChatPeerUserId();
    if (peerId == null)
    {
      return null;
    }

    const cached = this.findCachedUser(peerId);
    return cached?.photo ? `data:image/jpeg;base64,${cached.photo}` : null;
  }

  public getDirectChatPeerInitials(): string | null
  {
    const peerId = this.getDirectChatPeerUserId();
    if (peerId == null)
    {
      return null;
    }

    return this.getUserInitials(peerId, this.getDirectChatPeerDisplayName());
  }

  public getDirectChatPeerDisplayName(): string
  {
    if (!this.isDirectChat())
    {
      return this.selectedChat?.text ?? '';
    }

    const peerId = this.getDirectChatPeerUserId();
    if (peerId == null)
    {
      return this.selectedChat?.text ?? '';
    }

    const chatPeer = this.selectedChat?.users?.find((user: { userId?: number | string; firstName?: string; name?: string }) =>
      chatIdsMatch(user.userId, peerId));
    const cached = this.findCachedUser(peerId);
    if (chatPeer?.firstName)
    {
      return chatPeer.firstName;
    }

    if (cached?.name)
    {
      const first = cached.name.trim().split(/\s+/)[0];
      return first || cached.name;
    }

    if (chatPeer?.name)
    {
      const first = chatPeer.name.trim().split(/\s+/)[0];
      return first || chatPeer.name;
    }

    return this.selectedChat?.text ?? '';
  }

  public isDirectChatPeerOnline(): boolean
  {
    const peerId = this.getDirectChatPeerUserId();
    return peerId != null && this.onlineUserIds.has(String(peerId));
  }

  public getMessageById(messageId: string | number): Message | undefined
  {
    return this.chatHistory.find((message) => chatIdsMatch(message.id, messageId));
  }

  public getReplyToPreview(message: Message | null | undefined): ChatHistoryReplyPreview | null
  {
    const preview = (message as Message & { replyToPreview?: ChatHistoryReplyPreview })?.replyToPreview;
    if (!preview?.id)
    {
      return null;
    }

    return preview;
  }

  public getReplyQuoteAuthor(message: Message, replyToId: string | number): string
  {
    const loaded = this.getMessageById(replyToId);
    if (loaded)
    {
      return this.getMessageAuthorName(loaded);
    }

    return this.getReplyToPreview(message)?.authorName?.trim() ?? '';
  }

  public getReplyQuotePreviewText(message: Message, replyToId: string | number): string
  {
    const loaded = this.getMessageById(replyToId);
    if (loaded)
    {
      return this.getReplyPreviewText(loaded);
    }

    const content = this.getReplyToPreview(message)?.content ?? '';
    const text = content.replace(/\s+/g, ' ').trim();
    if (!text)
    {
      return '';
    }

    if (text.length <= this.replyPreviewMaxLength)
    {
      return text;
    }

    return `${text.slice(0, this.replyPreviewMaxLength - 3)}...`;
  }

  public hasReplyQuoteContent(message: Message, replyToId: string | number): boolean
  {
    return Boolean(this.getMessageById(replyToId) || this.getReplyToPreview(message));
  }

  public getMessageAuthorName(message: Message | null | undefined): string
  {
    const author = message?.author;
    if (!author)
    {
      return '';
    }

    if (author.name)
    {
      return author.name;
    }

    const authorId = author.id;
    const fromUsers = this.users.find((user: { id?: number | string; userId?: number | string; name?: string }) =>
      chatIdsMatch(user.id ?? user.userId, authorId));
    if (fromUsers?.name)
    {
      return fromUsers.name;
    }

    const fromChatUser = this.selectedChat?.users?.find((user: { id?: number | string; userId?: number | string; name?: string }) =>
      chatIdsMatch(user.id ?? user.userId, authorId));
    if (fromChatUser?.name)
    {
      return fromChatUser.name;
    }

    if (chatIdsMatch(authorId, this.currentUser?.id))
    {
      return this.currentUser?.name ?? '';
    }

    return '';
  }

  /** Id of the message being quoted when composing a reply (the selected message's id). */
  public getReplyTargetMessageId(message: Message | null | undefined): number | null
  {
    return getReplyTargetMessageId(message);
  }

  /** Parent message id stored on a reply (API field {@code replyToId}). */
  public getMessageReplyToId(message: Message | null | undefined): number | null
  {
    return getMessageReplyToId(message);
  }

  public getReplyPreviewText(message: Message | null | undefined): string
  {
    if (!message)
    {
      return '';
    }

    const text = this.getMessageCopyText(message).replace(/\s+/g, ' ').trim();
    if (!text)
    {
      return '';
    }

    if (text.length <= this.replyPreviewMaxLength)
    {
      return text;
    }

    return `${text.slice(0, this.replyPreviewMaxLength - 3)}...`;
  }

  public scrollToMessage(messageId: string | number | null | undefined, event?: Event): void
  {
    event?.stopPropagation();
    if (messageId == null)
    {
      return;
    }

    this.pendingScrollToMessageId = messageId;
    this.scrollToTargetLoadAttempts = 0;
    this.ensureMessageLoadedAndScroll(messageId);
  }

  public getEmojiCategoryKey(categoryId: string): string
  {
    return `CHAT-EMOJI-CATEGORY-${categoryId.toUpperCase()}`;
  }

  public getEmojiImageUrl(image: string): string
  {
    const normalizedBase = this.baseHref.endsWith('/') ? this.baseHref : `${this.baseHref}/`;
    return `${normalizedBase}${image}`;
  }

  public getMessageParts(message: Message): ChatMessagePart[]
  {
    const text = message?.text ?? (message as { content?: string })?.content ?? '';
    if (!text)
    {
      return [];
    }

    const cacheKey = `${message?.id ?? 'draft'}:${text}`;
    const cached = this.messagePartsCache.get(cacheKey);
    if (cached)
    {
      return cached;
    }

    const parts = this.emojiLookup.parseMessageParts(text);
    this.messagePartsCache.set(cacheKey, parts);
    return parts;
  }

  public trackMessagePart(index: number, part: ChatMessagePart): string
  {
    if (part.type === 'text')
    {
      return `text-${index}`;
    }

    return `emoji-${part.emoji.id ?? part.emoji.char ?? index}`;
  }

  public trackReactionGroup(index: number, group: ChatReactionGroup): string
  {
    return group.emojiKey || `reaction-${index}`;
  }

  public onChatEmojiMouseOver(event: MouseEvent): void
  {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.chatEmojiTooltip)
    {
      return;
    }

    const anchor = target.closest('.chat-history-emoji-anchor[title]');
    if (anchor instanceof HTMLElement)
    {
      this.chatEmojiTooltip.toggle(anchor, true);
    }
  }

  public onChatEmojiMouseOut(event: MouseEvent): void
  {
    const related = event.relatedTarget;
    if (related instanceof HTMLElement && related.closest('.chat-history-emoji-anchor[title]'))
    {
      return;
    }

    this.chatEmojiTooltip?.hide();
  }

  public onResized(): void
  {
    const adjustHeight = this.isWindow ? 64 : 138;
    this.chatHeight = `${window.innerHeight - adjustHeight}px`;
  }

  public onDelete(): void
  {
    this.chatService.deleteChatHistory(this.deleteHistoryItem.id).subscribe({
      next: () =>
      {
        this.clearPendingDeleteUi();
        this.messageHandlerService.displaySuccess('CHAT-MESSAGE-DELETED');
        this.deleteConfirmationOpened = false;
        this.chatHistory = this.chatHistory.filter((x: any) => x.id !== this.deleteHistoryItem.id);
      },
      error: (errorResponse) =>
      {
        this.clearPendingDeleteUi();
        this.deleteConfirmationOpened = false;
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public showTooltip(e: ElementRef): void
  {
    this.tooltipDir.toggle(e, true);
  }

  public hideTooltip(): void
  {
    this.tooltipDir.hide();
  }

  public isCurrentUser = (item: any): boolean =>
  {
    const aid = item?.author?.id;
    const uid = this.authenticationService.currentUser?.id;
    return aid != null && uid != null && String(aid) === String(uid);
  };

  public toggleReaction(message: Message, emojiKey: string, emojiForRecent?: ChatEmoji): void
  {
    const chatHistoryId = message?.id;
    if (chatHistoryId == null)
    {
      return;
    }

    const previousReactions = [...getMessageReactions(message)];
    this.applyOptimisticReactionToggle(message, emojiKey);
    this.closeEmojiPicker();

    this.chatService.toggleChatHistoryReaction(Number(chatHistoryId), emojiKey).subscribe({
      next: (reactions) =>
      {
        this.setMessageReactions(chatHistoryId, reactions);
        if (emojiForRecent)
        {
          this.rememberRecentEmoji(emojiForRecent);
        }
      },
      error: (errorResponse) =>
      {
        this.setMessageReactions(chatHistoryId, previousReactions);
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /** True when any chat/space has unsaved compose text (including the active chat). */
  public hasUnsavedComposeDrafts(): boolean
  {
    if (this.messageBoxInput?.value?.trim())
    {
      return true;
    }

    this.persistComposeDraftForActiveChat();

    for (const draft of this.composeDraftByChatId.values())
    {
      if (draft.text?.trim())
      {
        return true;
      }
    }

    return false;
  }

  /** Scroll to a message in the DOM, loading older pages first when it is not in the current window. */
  private ensureMessageLoadedAndScroll(messageId: string | number): void
  {
    if (!chatIdsMatch(this.pendingScrollToMessageId, messageId))
    {
      return;
    }

    const host = this.getChatHistoryHost();
    if (scrollToChatMessage(host, messageId, { behavior: 'auto' }))
    {
      this.clearPendingScrollTarget();
      return;
    }

    if (this.getMessageById(messageId))
    {
      this.waitForPendingScrollTargetInDom(messageId);
      return;
    }

    if (this.historyLoadingOlder)
    {
      return;
    }

    if (this.historyHasMoreOlder)
    {
      if (this.scrollToTargetLoadAttempts >= this.scrollToTargetMaxLoadAttempts)
      {
        this.failScrollToMessage();
        return;
      }

      this.scrollToTargetLoadAttempts++;
      this.loadOlderChatHistory();
      return;
    }

    this.failScrollToMessage();
  }

  private waitForPendingScrollTargetInDom(messageId: string | number): void
  {
    scrollToChatMessageWhenPresent(this.getChatHistoryHost(), messageId, {
      behavior: 'auto',
      shouldContinue: () => chatIdsMatch(this.pendingScrollToMessageId, messageId),
      onComplete: (found) =>
      {
        if (found)
        {
          this.clearPendingScrollTarget();
        }
      }
    });
  }

  private clearPendingScrollTarget(): void
  {
    this.pendingScrollToMessageId = null;
    this.scrollToTargetLoadAttempts = 0;
  }

  private scheduleScrollToPendingTarget(): void
  {
    const targetId = this.pendingScrollToMessageId;
    if (targetId == null)
    {
      return;
    }

    afterNextRender(() => this.ensureMessageLoadedAndScroll(targetId), { injector: this.injector });
  }

  private failScrollToMessage(): void
  {
    this.clearPendingScrollTarget();
    this.messageHandlerService.displayWarning('CHAT-REPLY-UNAVAILABLE');
  }

  private saveEditedMessage(): void
  {
    const messageBox = this.messageBoxInput;
    const editedText = messageBox?.value?.trim();
    const message = this.editingMessage;
    if (!editedText || !message?.id || !this.selectedChat?.id)
    {
      return;
    }

    const originalText = this.getMessageCopyText(message).trim();
    if (editedText === originalText)
    {
      this.cancelEdit();
      return;
    }

    this.stopTypingNotification(true);
    const replyToId = this.getMessageReplyToId(message);
    const chatMessageToPersist: any = {
      id: message.id,
      author: {
        id: this.currentUser.id,
        name: this.currentUser.name
      },
      content: editedText,
      chatDateTime: message.timestamp
        ? moment(message.timestamp).utc().toISOString()
        : moment().utc().toISOString()
    };

    if (replyToId != null)
    {
      chatMessageToPersist.replyToId = replyToId;
    }

    const scrollAnchor = this.captureChatScrollAnchor();
    this.chatService.addChatHistory(this.selectedChat.id, chatMessageToPersist).subscribe({
      next: (data: any) =>
      {
        const mapped = this.mapHistoryMessage(data);
        const index = this.chatHistory.findIndex((entry) => chatIdsMatch(entry.id, mapped.id));
        if (index >= 0)
        {
          this.chatHistory[index] = mapped;
          this.chatHistory = [...this.chatHistory];
        }

        this.clearMessagePartsCacheForMessage(mapped.id);
        this.editingMessage = null;
        this.clearPendingEditUi();
        messageBox.value = null;
        this.restoreComposeDraftForChat(this.selectedChat?.id);
        this.restoreChatScrollAnchor(scrollAnchor);
        this.flushDeferredHistoryReload();
        this.messageHandlerService.displaySuccess('CHAT-MESSAGE-UPDATED-SUCCESS');
        messageBox.focus();
        this.changeDetectorRef.markForCheck();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private getChatHistoryHost(): HTMLElement | null
  {
    return this.chatHistoryHost?.nativeElement ?? null;
  }

  private captureChatScrollAnchor(): { nearBottom: boolean; scrollTop: number } | null
  {
    return captureChatScrollAnchor(this.getChatHistoryHost());
  }

  private pinChatScrollToBottom(): void
  {
    pinChatScrollToBottom(this.getChatHistoryHost());
  }

  private setupMessageListResizeObserver(): void
  {
    if (typeof ResizeObserver === 'undefined')
    {
      return;
    }

    const content = this.chatHistoryHost?.nativeElement?.querySelector('.k-message-list-content');
    if (!(content instanceof HTMLElement))
    {
      return;
    }

    this.messageListResizeObserver?.disconnect();
    this.messageListResizeObserver = new ResizeObserver(() =>
    {
      if (this.stickScrollToBottom)
      {
        this.pinChatScrollToBottom();
      }
    });
    this.messageListResizeObserver.observe(content);
  }

  /**
   * After layout changes: pin to bottom if already there; otherwise restore prior scrollTop.
   */
  private restoreChatScrollAnchor(anchor: { nearBottom: boolean; scrollTop: number } | null): void
  {
    if (!anchor)
    {
      return;
    }

    const host = this.getChatHistoryHost();
    const apply = (): void => applyChatScrollAnchor(host, anchor);

    if (anchor.nearBottom)
    {
      this.stickScrollToBottom = true;
    }

    afterNextRender(() =>
    {
      apply();
      window.setTimeout(() =>
      {
        apply();
        this.stickScrollToBottom = false;
      }, 280);
    }, { injector: this.injector });
  }

  private getMessageTextarea(): HTMLTextAreaElement | null
  {
    const host = this.messageBoxEl?.nativeElement as HTMLElement | undefined;
    return host?.querySelector('textarea') ?? null;
  }

  private clearComposeInput(): void
  {
    if (this.messageBoxInput)
    {
      this.messageBoxInput.value = null;
    }

    this.replyToMessage = null;
  }

  private persistComposeDraftForActiveChat(): void
  {
    if (this.editingMessage)
    {
      return;
    }

    this.persistComposeDraftForChat(this.selectedChat?.id);
  }

  private clearMessagePartsCacheForMessage(messageId: string | number): void
  {
    const prefix = `${messageId}:`;
    for (const key of this.messagePartsCache.keys())
    {
      if (key.startsWith(prefix))
      {
        this.messagePartsCache.delete(key);
      }
    }
  }

  private persistComposeDraftForChat(chatId: number | string | null | undefined): void
  {
    if (chatId == null)
    {
      return;
    }

    const key = String(chatId);
    const text = this.messageBoxInput?.value ?? '';
    const replyToMessageId = this.getReplyTargetMessageId(this.replyToMessage) ?? undefined;
    if (!text.trim() && replyToMessageId == null)
    {
      this.composeDraftByChatId.delete(key);
      return;
    }

    this.composeDraftByChatId.set(key, { text, replyToMessageId });
  }

  private restoreComposeDraftForChat(chatId: number | string | null | undefined): void
  {
    if (chatId == null || !chatIdsMatch(this.selectedChat?.id, chatId))
    {
      return;
    }

    const draft = this.composeDraftByChatId.get(String(chatId));
    if (!draft)
    {
      return;
    }

    afterNextRender(() =>
    {
      if (!chatIdsMatch(this.selectedChat?.id, chatId))
      {
        return;
      }

      if (this.messageBoxInput)
      {
        this.messageBoxInput.value = draft.text;
      }

      if (draft.replyToMessageId != null)
      {
        this.replyToMessage = this.getMessageById(draft.replyToMessageId) ?? null;
      }

      this.changeDetectorRef.markForCheck();
    }, { injector: this.injector });
  }

  private getMessageCopyText(message: Message): string
  {
    const parts = this.getMessageParts(message);
    if (parts.length === 0)
    {
      return message?.text ?? (message as { content?: string })?.content ?? '';
    }

    return parts.map((part) =>
    {
      if (part.type === 'text')
      {
        return part.value;
      }

      return part.emoji.char ?? ChatEmojiLookup.getInsertText(part.emoji);
    }).join('');
  }

  private resetEmojiSearch(): void
  {
    this.emojiSearchTerm = '';
    this.updateFilteredEmojis();
  }

  private updateFilteredEmojis(): void
  {
    const term = this.emojiSearchTerm.trim().toLowerCase();
    if (!term)
    {
      this.filteredEmojiCategories = this.chatEmojiCategories;
      return;
    }

    this.filteredEmojiCategories = this.chatEmojiCategories
      .map((category) => ({
        ...category,
        emojis: category.emojis.filter((emoji) => this.matchesEmojiSearch(emoji, term, category.id))
      }))
      .filter((category) => category.emojis.length > 0);
  }

  private matchesEmojiSearch(emoji: ChatEmoji, term: string, categoryId: string): boolean
  {
    if (emoji.char?.includes(term))
    {
      return true;
    }

    if (emoji.id?.toLowerCase().includes(term))
    {
      return true;
    }

    if (emoji.en.toLowerCase().includes(term) || emoji.es.toLowerCase().includes(term))
    {
      return true;
    }

    const categoryLabel = this.translate.instant(this.getEmojiCategoryKey(categoryId));
    return typeof categoryLabel === 'string' && categoryLabel.toLowerCase().includes(term);
  }

  private loadRecentEmojis(): void
  {
    const stored = this.storageService.retrieve(this.recentEmojisStorageKey);
    if (!Array.isArray(stored))
    {
      this.recentEmojis = [];
      return;
    }

    this.recentEmojis = this.emojiLookup
      .resolveStoredKeys(stored.filter((item) => typeof item === 'string'))
      .slice(0, this.recentEmojisLimit);
  }

  private rememberRecentEmoji(emoji: ChatEmoji): void
  {
    const key = getEmojiKey(emoji);
    if (!key)
    {
      return;
    }

    const stored = this.storageService.retrieve(this.recentEmojisStorageKey);
    const keys = Array.isArray(stored) ? stored.filter((item) => typeof item === 'string') : [];
    const nextKeys = [key, ...keys.filter((item) => item !== key)].slice(0, this.recentEmojisLimit);

    this.storageService.store(this.recentEmojisStorageKey, nextKeys);
    this.recentEmojis = this.emojiLookup.resolveStoredKeys(nextKeys);
  }

  private setMessageReactions(chatHistoryId: string | number, reactions: ChatMessageReaction[]): void
  {
    const message = this.chatHistory.find((entry) => chatIdsMatch(entry.id, chatHistoryId));
    if (!message)
    {
      return;
    }

    const nextReactions = [...reactions];
    if (reactionsEqual(getMessageReactions(message), nextReactions))
    {
      return;
    }

    const scrollAnchor = this.captureChatScrollAnchor();
    (message as Message & { reactions?: ChatMessageReaction[] }).reactions = nextReactions;
    this.changeDetectorRef.markForCheck();
    this.restoreChatScrollAnchor(scrollAnchor);
  }

  private applyOptimisticReactionToggle(message: Message, emojiKey: string): void
  {
    const userId = this.authenticationService.currentUser?.id;
    if (userId == null)
    {
      return;
    }

    const reactions = [...getMessageReactions(message)];
    const existingIndex = reactions.findIndex((reaction) =>
      reaction.emojiKey === emojiKey && String(reaction.userId) === String(userId));

    if (existingIndex >= 0)
    {
      reactions.splice(existingIndex, 1);
    }
    else
    {
      reactions.push({
        emojiKey,
        userId,
        userName: this.currentUser?.name
      });
    }

    this.setMessageReactions(message.id, reactions);
  }

  private mapHistoryMessage(message: any): any
  {
    return mapHistoryMessage(message, {
      statusPlaceholder: this.STATUS_PLACEHOLDER,
      textChatUpdated: this.textChatUpdated,
      resolveAuthorAvatarUrl: (authorId, authorName) => this.buildAuthorAvatarUrl(authorId, authorName),
      resolveAuthorName: (authorId) => this.findCachedUser(authorId)?.name
    });
  }

  private handleWsChatTyping(stompMessage: { body?: string }): void
  {
    if (!this.selectedChat)
    {
      return;
    }

    const payload = this.parseChatTypingPayload(stompMessage);
    if (!payload || !chatIdsMatch(payload.chatId, this.selectedChat.id))
    {
      return;
    }

    const currentUserId = this.authenticationService.currentUser?.id;
    if (currentUserId != null && chatIdsMatch(payload.userId, currentUserId))
    {
      return;
    }

    if (payload.typing)
    {
      this.setRemoteTypingIndicator(payload);
    }
    else
    {
      this.removeRemoteTypingIndicator(payload.userId);
    }
  }

  private parseChatTypingPayload(stompMessage: { body?: string }): {
    chatId: number | string;
    userId: number | string;
    userName?: string;
    typing: boolean;
  } | null
  {
    try
    {
      const raw = stompMessage?.body;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!parsed || parsed.chatId == null || parsed.userId == null)
      {
        return null;
      }

      return {
        chatId: parsed.chatId,
        userId: parsed.userId,
        userName: parsed.userName,
        typing: Boolean(parsed.typing)
      };
    }
    catch
    {
      return null;
    }
  }

  private setRemoteTypingIndicator(payload: {
    userId: number | string;
    userName?: string;
  }): void
  {
    this.remoteTypingByUserId.set(String(payload.userId), {
      userId: payload.userId,
      userName: payload.userName
    });
    this.applyRemoteTypingIndicatorsToHistory();
    this.scheduleRemoteTypingExpiry(payload.userId);
  }

  private removeRemoteTypingIndicator(userId: number | string): void
  {
    if (this.remoteTypingByUserId.delete(String(userId)))
    {
      this.applyRemoteTypingIndicatorsToHistory();
    }

    this.clearRemoteTypingExpiry(userId);
  }

  /** Synthetic history rows (no typing:true) so Kendo renders one row per user via the message template. */
  private applyRemoteTypingIndicatorsToHistory(): boolean
  {
    const withoutTyping = this.chatHistory.filter((message) => !this.isTypingMessage(message));
    const typingMessages = [...this.remoteTypingByUserId.values()].map((entry) =>
      this.buildRemoteTypingMessage(entry.userId, entry.userName));
    const currentTyping = this.chatHistory.filter((message) => this.isTypingMessage(message));
    const sameTyping = currentTyping.length === typingMessages.length
      && typingMessages.every((typing, index) =>
        chatIdsMatch(typing.author?.id, currentTyping[index]?.author?.id));

    if (sameTyping)
    {
      return false;
    }

    this.chatHistory = [...withoutTyping, ...typingMessages];
    this.changeDetectorRef.markForCheck();
    return true;
  }

  private buildRemoteTypingMessage(userId: number | string, userName?: string): Message
  {
    const cached = this.findCachedUser(userId);
    const chatPeer = this.selectedChat?.users?.find((user: { userId?: number | string; name?: string }) =>
      chatIdsMatch(user.userId, userId));

    return {
      id: this.getTypingMessageId(userId),
      author: {
        id: userId,
        name: userName ?? chatPeer?.name ?? cached?.name,
        avatarUrl: this.buildAuthorAvatarUrl(userId, userName)
      }
    };
  }

  private handleComposeFocusChange(): void
  {
    const active = document.activeElement as HTMLElement | null;
    if (this.isComposeAreaFocused(active))
    {
      this.innerInputFocus = true;
      if (this.typingNotificationActive)
      {
        this.resetTypingIdleTimer();
      }
      return;
    }

    this.innerInputFocus = false;
    if (!this.messageBoxInput?.value?.trim())
    {
      this.stopTypingNotification(true);
    }
  }

  private isComposeAreaFocused(active: HTMLElement | null): boolean
  {
    if (!active)
    {
      return false;
    }

    const composeRoot = this.messageBoxEl?.nativeElement?.closest('.chat-compose-box');
    if (composeRoot?.contains(active))
    {
      return true;
    }

    return Boolean(active.closest('.chat-emoji-picker, .chat-emoji-picker-wrap'));
  }

  private scheduleRemoteTypingExpiry(userId: number | string): void
  {
    const key = String(userId);
    this.clearRemoteTypingExpiry(userId);
    this.typingRemoteExpiryTimers.set(key, setTimeout(() =>
    {
      this.typingRemoteExpiryTimers.delete(key);
      this.removeRemoteTypingIndicator(userId);
    }, this.typingRemoteExpiryMs));
  }

  private clearRemoteTypingExpiry(userId: number | string): void
  {
    const key = String(userId);
    const timer = this.typingRemoteExpiryTimers.get(key);
    if (timer)
    {
      clearTimeout(timer);
      this.typingRemoteExpiryTimers.delete(key);
    }
  }

  private clearRemoteTypingIndicators(): void
  {
    [...this.typingRemoteExpiryTimers.keys()].forEach((userId) => this.clearRemoteTypingExpiry(userId));
    this.remoteTypingByUserId.clear();
    this.chatHistory = this.chatHistory.filter((message) => !this.isTypingMessage(message));
  }

  private scheduleTypingNotification(typing: boolean): void
  {
    if (!this.selectedChat)
    {
      return;
    }

    if (this.typingSendTimer)
    {
      clearTimeout(this.typingSendTimer);
      this.typingSendTimer = null;
    }

    if (typing)
    {
      this.typingSendTimer = setTimeout(() =>
      {
        this.typingSendTimer = null;
        this.sendTypingNotification(true);
        this.resetTypingIdleTimer();
      }, this.typingSendDebounceMs);
    }
    else
    {
      this.stopTypingNotification(true);
    }
  }

  private resetTypingIdleTimer(): void
  {
    if (this.typingIdleTimer)
    {
      clearTimeout(this.typingIdleTimer);
    }

    this.typingIdleTimer = setTimeout(() => this.stopTypingNotification(true), this.typingIdleMs);
  }

  private sendTypingNotification(typing: boolean): void
  {
    if (!this.selectedChat)
    {
      return;
    }

    if (typing)
    {
      if (!this.typingNotificationActive)
      {
        this.typingNotificationActive = true;
        this.publishTypingState(true);
        this.startTypingHeartbeat();
      }

      this.resetTypingIdleTimer();
      return;
    }

    if (!this.typingNotificationActive)
    {
      return;
    }

    this.typingNotificationActive = false;
    this.stopTypingHeartbeat();
    this.publishTypingState(false);
  }

  private publishTypingState(typing: boolean): void
  {
    if (!this.selectedChat)
    {
      return;
    }

    this.chatService.sendChatTyping(this.selectedChat.id, typing).subscribe({
      error: () =>
      {
        if (typing)
        {
          this.typingNotificationActive = false;
          this.stopTypingHeartbeat();
        }
        else
        {
          this.typingNotificationActive = true;
        }
      }
    });
  }

  private startTypingHeartbeat(): void
  {
    this.stopTypingHeartbeat();
    this.typingHeartbeatTimer = setInterval(() =>
    {
      if (!this.typingNotificationActive || !this.selectedChat)
      {
        this.stopTypingHeartbeat();
        return;
      }

      this.publishTypingState(true);
    }, this.typingHeartbeatMs);
  }

  private stopTypingHeartbeat(): void
  {
    if (this.typingHeartbeatTimer)
    {
      clearInterval(this.typingHeartbeatTimer);
      this.typingHeartbeatTimer = null;
    }
  }

  private stopTypingNotification(immediate: boolean): void
  {
    if (this.typingSendTimer)
    {
      clearTimeout(this.typingSendTimer);
      this.typingSendTimer = null;
    }

    if (this.typingIdleTimer)
    {
      clearTimeout(this.typingIdleTimer);
      this.typingIdleTimer = null;
    }

    if (!this.typingNotificationActive)
    {
      return;
    }

    if (immediate)
    {
      this.sendTypingNotification(false);
      return;
    }

    this.scheduleTypingNotification(false);
  }

  private getDirectChatPeerUserId(): number | string | null
  {
    if (!this.isDirectChat())
    {
      return null;
    }

    const currentUserId = this.authenticationService.currentUser?.id;
    const peer = this.selectedChat?.users?.find((user: { userId?: number | string }) =>
      user?.userId != null && !chatIdsMatch(user.userId, currentUserId));
    return peer?.userId ?? null;
  }

  private handleWsUsersPresence(stompMessage: { body?: string }): void
  {
    try
    {
      const raw = stompMessage?.body;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const userIds = Array.isArray(parsed?.userIds) ? parsed.userIds : [];
      this.applyOnlineUserIds(userIds);
    }
    catch
    {
      this.onlineStatusLoaded = false;
      this.refreshOnlineStatus(true);
    }
  }

  private applyOnlineUserIds(userIds: Array<number | string>): void
  {
    this.onlineUserIds = new Set(
      userIds.filter((id) => id != null).map((id) => String(id)));
    this.users?.forEach((user: { id?: number | string; online?: boolean }) =>
    {
      user.online = this.onlineUserIds.has(String(user.id));
    });
    this.changeDetectorRef.markForCheck();
  }

  private refreshOnlineStatus(force = false): void
  {
    if (!force && (this.onlineStatusLoaded || this.onlineStatusSubscription))
    {
      return;
    }

    this.onlineStatusSubscription?.unsubscribe();
    this.onlineStatusSubscription = this.userService.getAllOnlineUsers().subscribe({
      next: (response: any) =>
      {
        const onlineUsers = Array.isArray(response) ? response : (response?.data ?? []);
        this.applyOnlineUserIds(onlineUsers.map((user: { id?: number | string }) => user.id));
        this.onlineStatusLoaded = true;
      },
      complete: () =>
      {
        this.onlineStatusSubscription = null;
      },
      error: () =>
      {
        this.onlineStatusSubscription = null;
      }
    });
  }

  private getUserInitials(userId: number | string, fallbackName?: string): string | null
  {
    const chatPeer = this.selectedChat?.users?.find((user: { userId?: number | string; id?: number | string; firstName?: string; lastName?: string; name?: string }) =>
      chatIdsMatch(user.userId ?? user.id, userId));
    const cached = this.findCachedUser(userId);

    const firstName = (chatPeer?.firstName ?? cached?.firstName)?.trim();
    const lastName = (chatPeer?.lastName ?? cached?.lastName)?.trim();
    if (firstName && lastName)
    {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }

    const fullName = (cached?.name ?? chatPeer?.name ?? fallbackName)?.trim();
    if (fullName)
    {
      const parts = fullName.split(/\s+/).filter(Boolean);
      if (parts.length >= 2)
      {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }

      if (parts.length === 1)
      {
        return parts[0].charAt(0).toUpperCase();
      }
    }

    if (firstName)
    {
      return firstName.charAt(0).toUpperCase();
    }

    return null;
  }

  private buildAuthorAvatarUrl(
    userId: number | string,
    fallbackName?: string,
    pixelSize: number = this.chatAvatarPixelSize): string | null
  {
    const cached = this.findCachedUser(userId);
    if (cached?.photo)
    {
      return `data:image/jpeg;base64,${cached.photo}`;
    }

    const initials = this.getUserInitials(userId, fallbackName);
    if (initials)
    {
      return this.buildInitialsAvatarDataUrl(initials, pixelSize);
    }

    return null;
  }

  /** Canvas PNG at device-pixel size for crisp initials in Kendo avatars; header uses CSS text. */
  private buildInitialsAvatarDataUrl(initials: string, pixelSize: number): string
  {
    const safeInitials = initials.replace(/[<>&'"]/g, '').slice(0, 3);
    if (!safeInitials)
    {
      return '';
    }

    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
    const cacheKey = `${safeInitials}-${pixelSize}-${dpr}`;
    const cached = this.initialsAvatarUrlCache.get(cacheKey);
    if (cached)
    {
      return cached;
    }

    if (typeof document === 'undefined')
    {
      return '';
    }

    const size = Math.round(pixelSize * dpr);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx)
    {
      return '';
    }

    const center = size / 2;
    const radius = size / 2;
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.fillStyle = '#e9ecef';
    ctx.fillRect(0, 0, size, size);

    const fontPx = Math.round(size * (safeInitials.length > 1 ? 0.36 : 0.44));
    ctx.fillStyle = '#3a5161';
    ctx.font = `600 ${fontPx}px "Segoe UI", Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(safeInitials, center, center);

    const dataUrl = canvas.toDataURL('image/png');
    this.initialsAvatarUrlCache.set(cacheKey, dataUrl);
    return dataUrl;
  }

  private getChatMemberFirstName(user: { userId?: number | string; firstName?: string; name?: string }): string
  {
    if (user.firstName)
    {
      return user.firstName;
    }

    const cached = user.userId != null ? this.findCachedUser(user.userId) : undefined;
    if (cached?.name)
    {
      const first = cached.name.trim().split(/\s+/)[0];
      return first || cached.name;
    }

    if (user.name)
    {
      const first = user.name.trim().split(/\s+/)[0];
      return first || user.name;
    }

    return '';
  }

  private findCachedUser(userId: number | string): {
    id?: number | string;
    name?: string;
    firstName?: string;
    lastName?: string;
    photo?: string;
  } | undefined
  {
    return this.users.find((user: {
      id?: number | string;
      userId?: number | string;
      name?: string;
      firstName?: string;
      lastName?: string;
      photo?: string;
    }) => chatIdsMatch(user.id ?? user.userId, userId));
  }

  private getTypingMessageId(userId: number | string): string
  {
    return `${this.typingMessageIdPrefix}${userId}`;
  }

  private isTypingMessage(message: Message): boolean
  {
    return message?.typing === true || String(message?.id ?? '').startsWith(this.typingMessageIdPrefix);
  }

  private handleWsChatCount(stompMessage: { body?: string }): void
  {
    if (!this.selectedChat)
    {
      return;
    }

    const counts = parseChatCountPayload(stompMessage);
    const entry = counts.find((count) => chatIdsMatch(count.chatId, this.selectedChat.id));
    if (entry)
    {
      this.handleReloadChatHistoryRequest(entry);
    }
  }

  private handleReloadChatHistoryRequest(data: { chatId?: number | string; unreadCount?: number }): void
  {
    if (!this.selectedChat || !data || !chatIdsMatch(data.chatId, this.selectedChat.id))
    {
      return;
    }

    // New activity on the open chat clears unread; Mark as unread suppresses this briefly.
    const suppressMarkRead = this.suppressMarkReadForChatId != null
      && chatIdsMatch(data.chatId, this.suppressMarkReadForChatId);
    const markRead = !suppressMarkRead;
    if (this.editingMessage)
    {
      this.historyReloadDeferred = true;
      this.deferredHistoryReloadMarkRead = this.deferredHistoryReloadMarkRead || markRead;
      return;
    }

    this.loadChatHistoryFromServer(markRead);
  }

  private clearDeferredHistoryReload(): void
  {
    this.historyReloadDeferred = false;
    this.deferredHistoryReloadMarkRead = false;
  }

  private flushDeferredHistoryReload(): void
  {
    if (!this.historyReloadDeferred || !this.selectedChat)
    {
      this.clearDeferredHistoryReload();
      return;
    }

    const markRead = this.deferredHistoryReloadMarkRead;
    this.scrollAnchorAfterHistoryLoad = this.captureChatScrollAnchor();
    this.clearDeferredHistoryReload();
    this.loadChatHistoryFromServer(markRead);
  }

  private mergeHistoryMessages(serverMessages: Message[]): Message[]
  {
    const persistedHistory = this.chatHistory.filter((message) => !this.isTypingMessage(message));
    const scrollAnchor = this.captureChatScrollAnchor();

    if (persistedHistory.length === 0)
    {
      this.chatHistory = serverMessages;
      this.applyRemoteTypingIndicatorsToHistory();
      this.restoreChatScrollAnchor(this.captureChatScrollAnchor() ?? { nearBottom: true, scrollTop: 0 });
      return this.chatHistory;
    }

    const mergeResult = computeHistoryMerge(persistedHistory, serverMessages);
    if (mergeResult.structureChanged)
    {
      this.chatHistory = mergeResult.nextHistory;
    }
    else if (mergeResult.patchedInPlace)
    {
      this.changeDetectorRef.markForCheck();
    }

    const typingBefore = this.applyRemoteTypingIndicatorsToHistory();
    const historyChanged = mergeResult.structureChanged || mergeResult.patchedInPlace || typingBefore;

    if (historyChanged)
    {
      this.restoreChatScrollAnchor(scrollAnchor);
    }

    return this.chatHistory;
  }

  private resolveRecentEmojisLimit(): number
  {
    const limit = this.appConfigService.getConfig()?.chatRecentEmojisLimit;
    return typeof limit === 'number' && limit > 0 ? limit : 10;
  }

  /** Clears the temporary class that keeps timestamp/meta visible while the delete dialog is open. */
  private clearPendingDeleteUi(): void
  {
    this.pendingDeleteMessageRow?.classList.remove('chat-history-pending-delete');
    this.pendingDeleteMessageRow = null;
  }

  /** Keeps the message meta row visible while the reaction emoji picker has focus. */
  private markPendingReactionRow(event: Event): void
  {
    this.clearPendingReactionUi();
    const target = event.target;
    if (target instanceof HTMLElement)
    {
      const row = target.closest<HTMLElement>('.k-message');
      if (row)
      {
        row.classList.add('chat-history-pending-reaction');
        this.pendingReactionMessageRow = row;
      }
    }
  }

  private clearPendingReactionUi(): void
  {
    this.pendingReactionMessageRow?.classList.remove('chat-history-pending-reaction');
    this.pendingReactionMessageRow = null;
  }

  /** Keeps the message meta row visible while the message is being edited in the compose box. */
  private markPendingEditRow(event: Event): void
  {
    this.clearPendingEditUi();
    const target = event.target;
    if (target instanceof HTMLElement)
    {
      const row = target.closest<HTMLElement>('.k-message');
      if (row)
      {
        row.classList.add('chat-history-pending-edit');
        this.pendingEditMessageRow = row;
      }
    }
  }

  private clearPendingEditUi(): void
  {
    this.pendingEditMessageRow?.classList.remove('chat-history-pending-edit');
    this.pendingEditMessageRow = null;
  }

  /** Re-render bubbles after the emoji catalog loads so tokens/unicode are parsed for tooltips. */
  private refreshMessageEmojiRendering(): void
  {
    this.messagePartsCache.clear();
    if (this.chatHistory.length > 0)
    {
      this.chatHistory = [...this.chatHistory];
    }
  }

  private loadChatHistory(callRefreshChatEvent: boolean): void
  {
    this.loadChatHistoryFromServer(callRefreshChatEvent);
  }

  /** Clears unread for the open chat in the list and on the server (via chat-list handler). */
  private markSelectedChatRead(): void
  {
    if (!this.selectedChat?.id)
    {
      return;
    }

    this.messengerService.sendMessage(new MessageSubject('refreshChatUnread', [{
      chatId: this.selectedChat.id,
      unreadCount: 0
    }]));
  }

  /** Reloads open chat from API; merges when history already loaded to preserve scroll. */
  private loadChatHistoryFromServer(markReadAfterNewMessages: boolean): void
  {
    if (!this.selectedChat)
    {
      this.chatHistory = [];
      this.historyHasMoreOlder = false;
      return;
    }

    const chatId = this.selectedChat.id;
    const generation = ++this.historyLoadGeneration;
    const isInitialLoad = this.chatHistory.filter((message) => !this.isTypingMessage(message)).length === 0;
    if (isInitialLoad)
    {
      this.messagePartsCache.clear();
    }

    this.chatService.getChatHistoryPage(chatId, { limit: this.resolveHistoryPageSize() }).subscribe({
      next: (page) =>
      {
        if (generation !== this.historyLoadGeneration)
        {
          return;
        }

        const mapped = (page.data ?? []).map((message) => this.mapHistoryMessage(message));
        this.historyHasMoreOlder = page.hasMore;

        if (isInitialLoad)
        {
          this.chatHistory = mapped;
          this.applyRemoteTypingIndicatorsToHistory();
          this.restoreChatScrollAnchor(this.captureChatScrollAnchor() ?? { nearBottom: true, scrollTop: 0 });
          this.changeDetectorRef.markForCheck();
        }
        else
        {
          this.mergeHistoryMessages(mapped);
        }

        this.restoreComposeDraftForChat(chatId);

        if (this.scrollAnchorAfterHistoryLoad)
        {
          this.restoreChatScrollAnchor(this.scrollAnchorAfterHistoryLoad);
          this.scrollAnchorAfterHistoryLoad = null;
        }

        if (markReadAfterNewMessages)
        {
          this.messengerService.sendMessage(new MessageSubject('refreshChatUnread',
            [{
              chatId: this.selectedChat.id,
              unreadCount: 0
            }]));
        }

        afterNextRender(() =>
        {
          this.setupMessageListResizeObserver();
          this.setupHistoryScrollListener();
        }, { injector: this.injector });
      },
      error: (errorResponse) =>
      {
        if (generation === this.historyLoadGeneration && isInitialLoad)
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      }
    });
  }

  private loadOlderChatHistory(afterPrepend?: () => void): void
  {
    if (!this.selectedChat || this.historyLoadingOlder || !this.historyHasMoreOlder)
    {
      return;
    }

    const beforeId = this.getOldestLoadedHistoryId();
    if (beforeId == null)
    {
      this.historyHasMoreOlder = false;
      return;
    }

    const chatId = this.selectedChat.id;
    const generation = this.historyLoadGeneration;
    const host = this.getChatHistoryHost();
    const preserveScrollPosition = this.pendingScrollToMessageId == null;
    const prependAnchor = preserveScrollPosition ? capturePrependScrollAnchor(host) : null;

    this.historyLoadingOlder = true;
    this.chatService.getChatHistoryPage(chatId, {
      limit: this.resolveHistoryPageSize(),
      beforeId
    }).subscribe({
      next: (page) =>
      {
        if (generation !== this.historyLoadGeneration)
        {
          return;
        }

        this.historyHasMoreOlder = page.hasMore;
        const mapped = (page.data ?? []).map((message) => this.mapHistoryMessage(message));
        this.prependOlderHistoryMessages(mapped, prependAnchor);
        this.changeDetectorRef.markForCheck();
        afterPrepend?.();
      },
      error: (errorResponse) =>
      {
        if (generation === this.historyLoadGeneration)
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      },
      complete: () =>
      {
        if (generation === this.historyLoadGeneration)
        {
          this.historyLoadingOlder = false;
          this.changeDetectorRef.markForCheck();
          this.scheduleScrollToPendingTarget();
        }
      }
    });
  }

  private prependOlderHistoryMessages(olderMessages: Message[], prependAnchor: ReturnType<typeof capturePrependScrollAnchor>): void
  {
    if (olderMessages.length === 0)
    {
      return;
    }

    const persisted = this.chatHistory.filter((message) => !this.isTypingMessage(message));
    const typing = this.chatHistory.filter((message) => this.isTypingMessage(message));
    const existingIds = new Set(persisted.map((message) => String(message.id)));
    const uniqueOlder = olderMessages.filter((message) => message.id != null && !existingIds.has(String(message.id)));

    if (uniqueOlder.length === 0)
    {
      if ((olderMessages?.length ?? 0) > 0)
      {
        this.historyHasMoreOlder = false;
      }

      return;
    }

    this.chatHistory = [...uniqueOlder, ...persisted, ...typing];

    afterNextRender(() =>
    {
      if (this.pendingScrollToMessageId != null)
      {
        return;
      }

      if (prependAnchor)
      {
        restorePrependScrollAnchor(this.getChatHistoryHost(), prependAnchor);
        window.setTimeout(() => restorePrependScrollAnchor(this.getChatHistoryHost(), prependAnchor), 0);
      }
    }, { injector: this.injector });
  }

  private getOldestLoadedHistoryId(): number | null
  {
    const messages = this.chatHistory.filter((message) => !this.isTypingMessage(message) && message.id != null);
    if (messages.length === 0)
    {
      return null;
    }

    let oldestId = Number(messages[0].id);
    if (!Number.isFinite(oldestId))
    {
      return null;
    }

    for (const message of messages)
    {
      const id = Number(message.id);
      if (Number.isFinite(id) && id < oldestId)
      {
        oldestId = id;
      }
    }

    return oldestId;
  }

  private resolveHistoryPageSize(): number
  {
    const configured = this.appConfigService.getConfig()?.chatHistoryPageSize;
    if (typeof configured === 'number' && configured > 0)
    {
      return configured;
    }

    return 50;
  }

  private setupHistoryScrollListener(): void
  {
    this.teardownHistoryScrollListener();
    const scrollElement = getChatScrollElement(this.getChatHistoryHost());
    if (!scrollElement)
    {
      return;
    }

    this.historyScrollHandler = (): void => this.onHistoryScroll();
    scrollElement.addEventListener('scroll', this.historyScrollHandler, { passive: true });
  }

  private teardownHistoryScrollListener(): void
  {
    const scrollElement = getChatScrollElement(this.getChatHistoryHost());
    if (scrollElement && this.historyScrollHandler)
    {
      scrollElement.removeEventListener('scroll', this.historyScrollHandler);
    }

    this.historyScrollHandler = null;
  }

  private onHistoryScroll(): void
  {
    if (!this.historyHasMoreOlder || this.historyLoadingOlder || !this.selectedChat)
    {
      return;
    }

    const scrollElement = getChatScrollElement(this.getChatHistoryHost());
    if (!scrollElement || scrollElement.scrollTop > 120)
    {
      return;
    }

    this.loadOlderChatHistory();
  }
}
